import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, XCircle, Loader2, Vote, Clock } from 'lucide-react';

const parseServerTime = (s) => new Date(/(?:Z|[+-]\d{2}:?\d{2})$/.test(s) ? s : s + 'Z');

export default function SelfScanVote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [responseType, setResponseType] = useState('candidate');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const loadToken = async () => {
      try {
        const { data: d } = await API.get(`/vote/${token}`);
        setData(d);
        const exp = parseServerTime(d.expires_at);
        setTimeLeft(Math.max(0, Math.floor((exp - Date.now()) / 1000)));
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired link');
      }
      setLoading(false);
    };
    loadToken();
  }, [token]);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let lat = null, lng = null, acc = null;
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
        );
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
        acc = pos.coords.accuracy;
      } catch {}

      await API.post(`/vote/${token}`, {
        candidate_id: responseType === 'candidate' ? selected : null,
        response_type: responseType,
        latitude: lat, longitude: lng, geo_accuracy: acc,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed');
    }
    setSubmitting(false);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Link Invalid</h2>
            <p data-testid="vote-error" className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 data-testid="vote-success" className="text-xl font-bold text-slate-900 mb-2">Vote Recorded</h2>
            <p className="text-slate-500">Your response has been submitted successfully.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4">
      <div className="max-w-md mx-auto">
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">
                <Vote className="w-5 h-5 inline mr-2 text-blue-500" />
                {data.position_name}
              </CardTitle>
              {timeLeft !== null && timeLeft > 0 && (
                <span data-testid="vote-timer" className="flex items-center gap-1 text-sm text-amber-600 font-mono">
                  <Clock className="w-4 h-4" />
                  {formatTime(timeLeft)}
                </span>
              )}
            </div>
            <p className="text-slate-500 text-sm mt-1">Voter: <strong>{data.voter_name}</strong></p>
          </CardHeader>
        </Card>

        {timeLeft === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-slate-700 font-semibold">Session Expired</p>
              <p className="text-slate-500 text-sm">Please ask the surveyor for a new QR code.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-2 mb-4">
              {data.candidates.map((c) => (
                <button
                  key={c.id}
                  data-testid={`vote-candidate-${c.id}`}
                  onClick={() => { setSelected(c.id); setResponseType('candidate'); }}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                    selected === c.id && responseType === 'candidate'
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: c.party_color || '#94a3b8' }}
                    />
                    <div>
                      <p className="font-semibold text-slate-900">{c.full_name}</p>
                      <p className="text-xs text-slate-500">{c.party_name}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex gap-2 mb-6">
              <Button
                data-testid="vote-abstain-btn"
                variant={responseType === 'abstain' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => { setResponseType('abstain'); setSelected(null); }}
              >
                Abstain
              </Button>
              <Button
                data-testid="vote-undecided-btn"
                variant={responseType === 'undecided' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => { setResponseType('undecided'); setSelected(null); }}
              >
                Undecided
              </Button>
            </div>

            <Button
              data-testid="vote-submit-btn"
              onClick={handleSubmit}
              disabled={submitting || (responseType === 'candidate' && !selected)}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12 text-base"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
              {submitting ? 'Submitting...' : 'Confirm Vote'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
